// modules/admin/AssignDeliveryCompanyModal.js
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid,
    Card,
    CardContent,
    Divider,
    Stack,
    IconButton,
    Chip,
    Paper,
} from '@mui/material';
import {
    Close as CloseIcon,
    LocalShipping as ShippingIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { NumberInput, useNotify } from 'react-admin';

const AssignDeliveryCompanyModal = ({
    open,
    onClose,
    selectedNeed,
    type = 'CROP', // 'CROP' or 'SERVICE'
    token,
}) => {
    const notify = useNotify();
    const [loading, setLoading] = useState(false);
    const [deliveryCompanies, setDeliveryCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [notes, setNotes] = useState('');
    const [quantity, setQuantity] = useState(null);
    const [assigning, setAssigning] = useState(false);

    // Fetch delivery companies
    useEffect(() => {
        const fetchDeliveryCompanies = async () => {
            try {
                setLoading(true);
                const response = await fetch(
                    `${process.env.REACT_APP_URL}/admin/in-kind/delivery-companies`,
                    {
                        method: 'GET',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            Authorization: 'Bearer ' + token,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch delivery companies');
                }

                const data = await response.json();
                setDeliveryCompanies(data);
            } catch (error) {
                console.error('Error fetching delivery companies:', error);
                notify('Error loading delivery companies', { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            fetchDeliveryCompanies();
        }
    }, [open, token, notify]);

    const handleAssignDelivery = async () => {
        if (!selectedCompany) {
            notify('Please select a delivery company', { type: 'warning' });
            return;
        }

        try {
            setAssigning(true);

            const payload = {
                itemId: selectedNeed.id,
                type: type,
                // quantity: selectedNeed.quantity || 1,
                quantity: quantity,
                deliveryCompanyId: selectedCompany,
                notes: notes,
            };

            const response = await fetch(
                `${process.env.REACT_APP_URL}/admin/in-kind/negotiation`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to assign delivery company');
            }

            const result = await response.json();

            notify('Successfully assigned delivery company!', { type: 'success' });
            onClose(true); // Pass true to indicate success and refresh parent

        } catch (error) {
            console.error('Error assigning delivery company:', error);
            notify(error.message || 'Failed to assign delivery company', { type: 'error' });
        } finally {
            setAssigning(false);
        }
    };

    const getNeedTypeLabel = () => {
        return type === 'CROP' ? 'Crop Need' : 'Service Need';
    };

    const getNeedDetails = () => {
        if (!selectedNeed) return {};

        if (type === 'CROP') {
            return {
                title: selectedNeed.offer || selectedNeed.subCategory?.name || 'Crop',
                category: selectedNeed.subCategory?.name || 'Category',
                quantity: `${selectedNeed.quantity || ''} ${selectedNeed._unit?.name || ''}`.trim(),
                location: `${selectedNeed._state?.name || ''}, ${selectedNeed._city?.name || ''}`,
                requester: selectedNeed.user?.name || 'Unknown',
                phone: selectedNeed.user?.phonenumber || 'N/A',
                description: selectedNeed.description || 'No description',
            };
        } else {
            return {
                title: selectedNeed.description || selectedNeed.service?.name || 'Service',
                category: selectedNeed.service?.name || 'Service Type',
                quantity: selectedNeed.donation_capacity || selectedNeed.quantity || '1',
                location: `${selectedNeed._state?.name || ''}, ${selectedNeed._city?.name || ''}`,
                requester: selectedNeed.user?.name || 'Unknown',
                phone: selectedNeed.user?.phonenumber || 'N/A',
                description: selectedNeed.description || 'No description',
            };
        }
    };

    const needDetails = getNeedDetails();

    return (
        <Modal
            open={open}
            onClose={() => !assigning && onClose(false)}
            aria-labelledby="assign-delivery-modal"
            maxWidth="md"
            fullWidth
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 800,
                    maxHeight: '90vh',
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 0,
                }}
            >
                {/* Header */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        background: '#613917',
                        color: 'white',
                        position: 'relative',
                    }}
                >
                    <IconButton
                        onClick={() => !assigning && onClose(false)}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'white',
                        }}
                        disabled={assigning}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <ShippingIcon sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography variant="h5" fontWeight="bold">
                                Assign Delivery Company
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Assign a delivery company to fulfill this {getNeedTypeLabel()}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* Content */}
                <Box sx={{ p: 3 }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {/* Need Details */}
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom fontWeight="bold" color="warning.main">
                                            {getNeedTypeLabel()} Details
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2">
                                                    <strong>Item:</strong> {needDetails.title}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2">
                                                    <strong>{type === 'CROP' ? 'Category' : 'Service Type'}:</strong> {needDetails.category}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2">
                                                    <strong>{type === 'CROP' ? 'Quantity Needed' : 'Required Capacity'}:</strong> {needDetails.quantity}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <LocationIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{needDetails.location}</Typography>
                                                </Stack>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PersonIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{needDetails.requester}</Typography>
                                                </Stack>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PhoneIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{needDetails.phone}</Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                        {needDetails.description && (
                                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Description:</strong> {needDetails.description}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Delivery Company Selection */}
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel id="delivery-company-label">
                                        Select Delivery Company
                                    </InputLabel>
                                    <Select
                                        labelId="delivery-company-label"
                                        value={selectedCompany}
                                        label="Select Delivery Company"
                                        onChange={(e) => setSelectedCompany(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <MenuItem value="">
                                            <em>Select a delivery company...</em>
                                        </MenuItem>
                                        {deliveryCompanies.map((company) => (
                                            <MenuItem key={company.id} value={company.id}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <ShippingIcon fontSize="small" color="action" />
                                                    <Typography>{company.name}</Typography>
                                                    {company.phone && (
                                                        <Chip
                                                            size="small"
                                                            label={company.phone}
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Selected Company Details */}
                            {selectedCompany && (
                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Selected Delivery Company Details:
                                            </Typography>
                                            {(() => {
                                                const company = deliveryCompanies.find(c => c.id === selectedCompany);
                                                if (!company) return null;

                                                return (
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body2">
                                                                <strong>Name:</strong> {company.name}
                                                            </Typography>
                                                        </Grid>
                                                        {company.phone && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2">
                                                                    <strong>Phone:</strong> {company.phone}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                        {company.email && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2">
                                                                    <strong>Email:</strong> {company.email}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                        {company.address && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2">
                                                                    <strong>Address:</strong> {company.address}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                        {company.created_at && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2">
                                                                    <strong>Registered:</strong>{' '}
                                                                    {new Date(company.created_at).toLocaleDateString()}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                );
                                            })()}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Notes */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Quantity to Deliver"
                                    value={quantity ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        // Allow empty field
                                        if (value === '') {
                                            setQuantity(null);
                                            return;
                                        }

                                        // Enforce integer only
                                        const intValue = parseInt(value, 10);
                                        if (!Number.isNaN(intValue) && intValue > 0) {
                                            setQuantity(intValue);
                                        }
                                    }}
                                    inputProps={{
                                        min: 1,
                                        step: 1,       // prevents decimals
                                    }}
                                    disabled={assigning}
                                />
                            </Grid>

                            {/* Notes */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Delivery Notes (Optional)"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add any special instructions for the delivery..."
                                    disabled={assigning}
                                />
                            </Grid>

                            {/* Information Alert */}
                            <Grid item xs={12}>
                                <Alert severity="info" icon={<CheckIcon />}>
                                    <Typography variant="body2">
                                        When you assign a delivery company:
                                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                            <li>A negotiation will be created automatically</li>
                                            <li>An invoice with amount 0 will be generated</li>
                                            <li>The invoice will be automatically accepted</li>
                                            <li>An order will be created for tracking</li>
                                            <li>Both parties will be notified</li>
                                        </ul>
                                    </Typography>
                                </Alert>
                            </Grid>
                        </Grid>
                    )}
                </Box>

                {/* Footer Actions */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    <Button
                        onClick={() => onClose(false)}
                        disabled={assigning}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAssignDelivery}
                        disabled={!selectedCompany || assigning}
                        variant="contained"
                        color="primary"
                        startIcon={
                            assigning ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <ShippingIcon />
                            )
                        }
                    >
                        {assigning ? 'Assigning...' : 'Assign Delivery Company'}
                    </Button>
                </Paper>
            </Box>
        </Modal>
    );
};

export default AssignDeliveryCompanyModal;